import { get, set, del, keys } from 'idb-keyval';

export const STORE_KEY = 'upload-queue-v1';
export const BUSTER_KEY = 'upload-queue-buster';

const UPLOADS_DIR = 'uploads';

const BLOB_PREFIX = 'upload-blob-';
let hasLoggedMissingEntries = false;

export const __resetLoggedMissingEntries = () => {
  hasLoggedMissingEntries = false;
};

export const storeBlob = async (itemId, blob) => {
  await set(`${BLOB_PREFIX}${itemId}`, blob);
};

export const loadBlob = async (itemId) => {
  return get(`${BLOB_PREFIX}${itemId}`);
};

export const removeBlob = async (itemId) => {
  await del(`${BLOB_PREFIX}${itemId}`);
};

const getUploadsDirectory = async (options) => {
  const root = await navigator.storage.getDirectory();
  if (!options) {
    return root.getDirectoryHandle(UPLOADS_DIR);
  }
  return root.getDirectoryHandle(UPLOADS_DIR, options);
};

const getItemDirectory = async (itemId, options) => {
  const uploads = await getUploadsDirectory(options);
  if (!options) {
    return uploads.getDirectoryHandle(itemId);
  }
  return uploads.getDirectoryHandle(itemId, options);
};

export const loadQueue = async () => {
  const items = await get(STORE_KEY);
  return items || [];
};

export const saveQueue = async (items) => {
  try {
    await set(STORE_KEY, items);
  } catch (error) {
    // IndexedDB replaces this value atomically. Never delete the previous snapshot to
    // make room: a second quota failure would erase every previously queued upload.
    // Callers must see the failure so enqueue rolls back and retains the selection.
    console.warn('Failed to persist upload queue:', error);
    throw error;
  }
};

export const clearQueue = async () => {
  await del(STORE_KEY);
};

export const checkBuster = async (version) => {
  const storedVersion = await get(BUSTER_KEY);

  if (!storedVersion) {
    await set(BUSTER_KEY, version);
    return false;
  }

  if (storedVersion !== version) {
    await clearQueue();
    await set(BUSTER_KEY, version);
    return true;
  }

  return false;
};

export const isOPFSAvailable = () =>
  typeof navigator !== 'undefined' &&
  typeof navigator.storage?.getDirectory === 'function';

export const writeFile = async (itemId, fileName, blob) => {
  const itemDirectory = await getItemDirectory(itemId, { create: true });
  const fileHandle = await itemDirectory.getFileHandle(fileName, {
    create: true,
  });
  const writable = await fileHandle.createWritable();
  try {
    await writable.write(blob);
    await writable.close();
  } catch (error) {
    try {
      if (typeof writable.abort === 'function') {
        await writable.abort();
      } else if (typeof writable.close === 'function') {
        await writable.close();
      }
    } catch {
      // Preserve the original write failure for callers.
    }
    throw error;
  }
};

export const readFile = async (itemId, fileName) => {
  const itemDirectory = await getItemDirectory(itemId);
  const fileHandle = await itemDirectory.getFileHandle(fileName);
  return fileHandle.getFile();
};

export const removeFile = async (itemId) => {
  const uploads = await getUploadsDirectory();
  await uploads.removeEntry(itemId, { recursive: true });
};

export const clearAllFiles = async (itemIds) => {
  let uploads;

  try {
    uploads = await getUploadsDirectory();
  } catch {
    // OPFS unavailable — still try IDB blob cleanup below.
  }

  await Promise.all(
    itemIds.map(async (itemId) => {
      if (uploads) {
        try {
          await uploads.removeEntry(itemId, { recursive: true });
        } catch {
          // Missing or already-removed upload directories should not block cleanup.
        }
      }

      try {
        await removeBlob(itemId);
      } catch {
        // IDB blob may not exist — keep going.
      }
    })
  );
};

export const sweepUnreferencedFiles = async (referencedItemIds) => {
  let uploads;

  try {
    uploads = await getUploadsDirectory();
  } catch {
    // OPFS unavailable — still try IDB blob sweep below.
  }

  const referenced = new Set(referencedItemIds);

  if (uploads && typeof uploads.entries === 'function') {
    for await (const [itemId] of uploads.entries()) {
      if (referenced.has(itemId)) {
        continue;
      }

      try {
        await uploads.removeEntry(itemId, { recursive: true });
      } catch {
        // Missing or locked directories can be retried on a later startup.
      }
    }
  } else if (!hasLoggedMissingEntries && uploads) {
    hasLoggedMissingEntries = true;
    console.debug(
      'Upload queue OPFS sweep skipped: directory entries are unavailable.'
    );
  }

  // Sweep unreferenced IDB blob fallback entries.
  try {
    const allKeys = await keys();
    const staleKeys = allKeys.filter(
      (key) =>
        typeof key === 'string' &&
        key.startsWith(BLOB_PREFIX) &&
        !referenced.has(key.slice(BLOB_PREFIX.length))
    );
    await Promise.all(staleKeys.map((key) => del(key)));
  } catch {
    // IDB not available — OPFS is the durable source of truth.
  }
};
